import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useOrbitAuth } from '../context/OrbitAuthContext';
import {
  House, Planet, Wallet, ChartLineUp, SignOut, List, X,
  ChartBar, Calculator, Sliders, Receipt, CurrencyDollar,
  Notepad, Rocket, MapPin, UsersThree, Command, Storefront,
  CreditCard, Buildings, AddressBook, UserCircle, Megaphone, Binoculars
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const OrbitLayout = ({ children }) => {
  const { t, language, toggleLanguage } = useLanguage();
  const { user, logout } = useOrbitAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSpace, setActiveSpace] = useState('myspace');
  const [pendingCount, setPendingCount] = useState(0);

  const isAdmin = user?.email === 'orbit.admin@demo.com';

  // Fetch pending business count for admin badge
  const fetchPendingCount = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const token = localStorage.getItem('orbit-token');
      const res = await fetch(`${API}/api/orbit/admin/businesses/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPendingCount(data.pending || 0);
      }
    } catch {}
  }, [isAdmin]);

  useEffect(() => { fetchPendingCount(); }, [fetchPendingCount]);
  // Refresh pending count every 60 seconds
  useEffect(() => {
    if (!isAdmin) return;
    const interval = setInterval(fetchPendingCount, 60000);
    return () => clearInterval(interval);
  }, [isAdmin, fetchPendingCount]);

  React.useEffect(() => {
    const adminPaths = ['/admin/calculator', '/admin/finances', '/admin/simulator', '/admin/expenses', '/admin/cashflow', '/admin/notes', '/admin/directory'];
    if (adminPaths.some(p => location.pathname.startsWith(p))) setActiveSpace('admin');
  }, [location.pathname]);

  const rankColors = {
    mercure: 'var(--planet-mercury)', venus: 'var(--planet-venus)',
    terre: 'var(--planet-earth)', mars: 'var(--planet-mars)',
    jupiter: 'var(--planet-jupiter)', saturne: 'var(--planet-saturn)'
  };
  const currentRankColor = rankColors[user?.rank] || 'var(--planet-mercury)';

  const mySpaceItems = [
    { path: '/dashboard', icon: House, label: language === 'fr' ? 'Tableau de bord' : 'Dashboard' },
    { path: '/dashboard/matrix', icon: Planet, label: language === 'fr' ? 'Mon Orbite' : 'My Orbit' },
    { path: '/dashboard/wallet', icon: Wallet, label: language === 'fr' ? 'Mon Portefeuille' : 'My Wallet' },
    { path: '/dashboard/commissions', icon: ChartLineUp, label: language === 'fr' ? 'Mes Commissions' : 'My Commissions' },
    { path: '/dashboard/team', icon: UsersThree, label: language === 'fr' ? 'Mon Equipe' : 'My Team' },
    { path: '/dashboard/business', icon: Storefront, label: language === 'fr' ? 'Mon Entreprise' : 'My Business' },
    { path: '/dashboard/pass', icon: CreditCard, label: language === 'fr' ? 'Mon Pass' : 'My Pass' },
    { path: '/dashboard/members', icon: AddressBook, label: language === 'fr' ? 'Annuaire' : 'Directory' },
    { path: '/dashboard/listings', icon: Megaphone, label: language === 'fr' ? 'Mes Annonces' : 'My Listings' },
    { path: '/dashboard/listings/browse', icon: Binoculars, label: language === 'fr' ? 'Petites Annonces' : 'Classifieds' },
    { path: '/dashboard/profile', icon: UserCircle, label: language === 'fr' ? 'Mon Profil' : 'My Profile' },
  ];

  const adminItems = isAdmin ? [
    { path: '/admin/directory', icon: Buildings, label: language === 'fr' ? 'Repertoire' : 'Directory', badge: pendingCount },
    { path: '/admin/calculator', icon: Calculator, label: language === 'fr' ? 'Calculatrice' : 'Calculator' },
    { path: '/admin/finances', icon: ChartBar, label: language === 'fr' ? 'Admin Finances' : 'Admin Finances' },
    { path: '/admin/simulator', icon: Sliders, label: language === 'fr' ? 'Simulateur' : 'Simulator' },
    { path: '/admin/expenses', icon: Receipt, label: language === 'fr' ? 'Depenses' : 'Expenses' },
    { path: '/admin/cashflow', icon: CurrencyDollar, label: 'Cash Flow' },
    { path: '/admin/notes', icon: Notepad, label: language === 'fr' ? 'Mes Notes' : 'My Notes' },
  ] : [];

  const navItems = activeSpace === 'admin' ? adminItems : mySpaceItems;

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const isActive = (path) => location.pathname === path;

  const rankNames = {
    mercure: { fr: 'Mercure', en: 'Mercury' }, venus: { fr: 'Venus', en: 'Venus' },
    terre: { fr: 'Terre', en: 'Earth' }, mars: { fr: 'Mars', en: 'Mars' },
    jupiter: { fr: 'Jupiter', en: 'Jupiter' }, saturne: { fr: 'Saturne', en: 'Saturn' }
  };
  const rankLabel = user?.rank_info
    ? (language === 'fr' ? user.rank_info.name_fr : user.rank_info.name_en)
    : (rankNames[user?.rank]?.[language] || 'Mercure');

  // Shared nav link renderer
  const NavLink = ({ item, onClick }) => (
    <Link to={item.path} onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
        isActive(item.path)
          ? (activeSpace === 'admin'
            ? 'bg-[var(--ls-gold)] text-white shadow-md shadow-[var(--ls-gold)]/20'
            : 'bg-[var(--ls-terracotta)] text-white shadow-md shadow-[var(--ls-terracotta)]/20')
          : 'text-white/70 hover:bg-white/10 hover:text-white'
      }`}
      data-testid={`orbit-nav-${item.path.split('/').pop()}`}>
      <item.icon className="w-5 h-5" weight={isActive(item.path) ? 'fill' : 'regular'} />
      <span className="font-medium flex-1">{item.label}</span>
      {item.badge > 0 && (
        <span className="min-w-[20px] h-5 flex items-center justify-center px-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full animate-pulse" data-testid="pending-badge">
          {item.badge}
        </span>
      )}
    </Link>
  );

  return (
    <div className="min-h-screen bg-[var(--ls-sand)]">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[var(--ls-bark)]/95 backdrop-blur-md border-b border-white/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[var(--ls-terracotta)] rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" weight="bold" />
            </div>
            <span className="font-heading font-bold text-white">Local<span className="text-[var(--ls-terracotta)]">Site</span>.Club</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider relative ${
              activeSpace === 'admin' ? 'bg-[var(--ls-gold)]/20 text-[var(--ls-gold)]' : 'bg-[var(--ls-terracotta)]/20 text-[var(--ls-terracotta)]'
            }`}>
              {activeSpace === 'admin' ? 'Admin' : (language === 'fr' ? 'Espace' : 'Space')}
              {pendingCount > 0 && activeSpace !== 'admin' && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] flex items-center justify-center px-0.5 bg-red-500 text-white text-[8px] font-bold rounded-full">{pendingCount}</span>
              )}
            </span>
            <div className="flex bg-white/10 rounded-md overflow-hidden text-xs">
              <button onClick={() => language !== 'fr' && toggleLanguage()}
                className={`px-2 py-1 transition-colors ${language === 'fr' ? 'bg-[var(--ls-terracotta)] text-white' : 'text-white/50'}`}>FR</button>
              <button onClick={() => language !== 'en' && toggleLanguage()}
                className={`px-2 py-1 transition-colors ${language === 'en' ? 'bg-[var(--ls-terracotta)] text-white' : 'text-white/50'}`}>EN</button>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white"
              data-testid="orbit-mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <List className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] bg-[var(--ls-bark)] flex flex-col" data-testid="mobile-menu-overlay">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-[var(--ls-terracotta)] rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" weight="bold" />
              </div>
              <div>
                <span className="font-heading font-bold text-white text-sm">Local<span className="text-[var(--ls-terracotta)]">Site</span>.Club</span>
                <p className="text-[10px] text-white/40">
                  {activeSpace === 'admin' ? 'Admin' : (language === 'fr' ? 'Espace Affilie' : 'Affiliate Space')}
                </p>
              </div>
            </div>
            <button onClick={() => setMobileMenuOpen(false)} className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 text-white">
              <X className="w-6 h-6" />
            </button>
          </div>

          {isAdmin && (
            <div className="px-5 pt-4 pb-2">
              <div className="flex bg-white/5 rounded-xl p-1 gap-1">
                <button onClick={() => setActiveSpace('myspace')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-bold transition-all ${
                    activeSpace === 'myspace' ? 'bg-[var(--ls-terracotta)] text-white shadow-md' : 'text-white/40'
                  }`}>
                  <Rocket className="w-4 h-4" />{language === 'fr' ? 'Mon Espace' : 'My Space'}
                </button>
                <button onClick={() => setActiveSpace('admin')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-bold transition-all ${
                    activeSpace === 'admin' ? 'bg-[var(--ls-gold)] text-white shadow-md' : 'text-white/40'
                  }`}>
                  <Command className="w-4 h-4" />Admin
                  {pendingCount > 0 && activeSpace !== 'admin' && (
                    <span className="min-w-[18px] h-[18px] flex items-center justify-center px-1 bg-red-500 text-white text-[9px] font-bold rounded-full animate-pulse">{pendingCount}</span>
                  )}
                </button>
              </div>
            </div>
          )}

          {user && (
            <div className="px-5 py-4">
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-base"
                  style={{ backgroundColor: currentRankColor }}>{user.name?.charAt(0)?.toUpperCase() || 'U'}</div>
                <div>
                  <p className="text-white font-semibold">{user.name}</p>
                  <div className="flex items-center gap-1.5">
                    <Planet className="w-3.5 h-3.5" style={{ color: currentRankColor }} weight="fill" />
                    <p className="text-sm" style={{ color: currentRankColor }}>{language === 'fr' ? `Orbite ${rankLabel}` : `${rankLabel} Orbit`}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <nav className="flex-1 px-5 space-y-1.5 overflow-y-auto">
            {navItems.map((item) => <NavLink key={item.path} item={item} onClick={() => setMobileMenuOpen(false)} />)}
          </nav>

          <div className="px-5 py-4 border-t border-white/10 space-y-3">
            <Link to="/" onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3.5 px-4 py-3 rounded-xl text-white/40 hover:text-white/70 text-sm">
              <MapPin className="w-4 h-4" /><span>LocalSite.Club</span>
            </Link>
            <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
              className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors"
              data-testid="orbit-logout-btn">
              <SignOut className="w-5 h-5" /><span className="font-medium">{language === 'fr' ? 'Deconnexion' : 'Logout'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-[var(--ls-bark)] overflow-y-auto">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 py-5 border-b border-white/8">
            <div className="w-10 h-10 bg-[var(--ls-terracotta)] rounded-xl flex items-center justify-center shadow-lg shadow-[var(--ls-terracotta)]/30">
              <MapPin className="w-6 h-6 text-white" weight="bold" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-white">Local<span className="text-[var(--ls-terracotta)]">Site</span>.Club</h1>
              <p className="text-xs text-white/40">
                {activeSpace === 'admin' ? 'Admin' : (language === 'fr' ? 'Espace Affilie' : 'Affiliate Space')}
              </p>
            </div>
          </div>

          {/* Space Toggle */}
          {isAdmin && (
            <div className="px-4 pt-4 pb-2">
              <div className="flex bg-white/5 rounded-xl p-1 gap-1" data-testid="space-toggle">
                <button onClick={() => setActiveSpace('myspace')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold transition-all ${
                    activeSpace === 'myspace' ? 'bg-[var(--ls-terracotta)] text-white shadow-md shadow-[var(--ls-terracotta)]/30' : 'text-white/40 hover:text-white/70'
                  }`} data-testid="toggle-myspace">
                  <Rocket className="w-4 h-4" />{language === 'fr' ? 'Mon Espace' : 'My Space'}
                </button>
                <button onClick={() => setActiveSpace('admin')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold transition-all ${
                    activeSpace === 'admin' ? 'bg-[var(--ls-gold)] text-white shadow-md shadow-[var(--ls-gold)]/30' : 'text-white/40 hover:text-white/70'
                  }`} data-testid="toggle-admin">
                  <Command className="w-4 h-4" />Admin
                  {pendingCount > 0 && activeSpace !== 'admin' && (
                    <span className="min-w-[18px] h-[18px] flex items-center justify-center px-1 bg-red-500 text-white text-[9px] font-bold rounded-full animate-pulse">{pendingCount}</span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* User Info */}
          {user && (
            <div className="px-6 py-4 border-b border-white/8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                  style={{ backgroundColor: currentRankColor }}>
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user.name}</p>
                  <div className="flex items-center gap-1">
                    <Planet className="w-3 h-3" style={{ color: currentRankColor }} weight="fill" />
                    <p className="text-xs truncate" style={{ color: currentRankColor }}>
                      {language === 'fr' ? `Orbite ${rankLabel}` : `${rankLabel} Orbit`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1">
            {navItems.map((item) => <NavLink key={item.path} item={item} />)}
          </nav>

          {/* Bottom */}
          <div className="px-4 py-4 border-t border-white/8 space-y-3">
            <div className="flex items-center justify-between px-4">
              <span className="text-sm text-white/40">{language === 'fr' ? 'Langue' : 'Language'}</span>
              <div className="flex bg-white/10 rounded-md overflow-hidden text-xs">
                <button onClick={() => language !== 'fr' && toggleLanguage()}
                  className={`px-2.5 py-1 transition-colors ${language === 'fr' ? 'bg-[var(--ls-terracotta)] text-white' : 'text-white/40'}`}
                  data-testid="orbit-lang-fr">FR</button>
                <button onClick={() => language !== 'en' && toggleLanguage()}
                  className={`px-2.5 py-1 transition-colors ${language === 'en' ? 'bg-[var(--ls-terracotta)] text-white' : 'text-white/40'}`}
                  data-testid="orbit-lang-en">EN</button>
              </div>
            </div>
            <Link to="/" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-white/40 hover:bg-white/5 hover:text-white/70 transition-colors text-sm">
              <MapPin className="w-4 h-4" /><span>LocalSite.Club</span>
            </Link>
            <button onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
              data-testid="orbit-logout-btn">
              <SignOut className="w-5 h-5" /><span className="font-medium">{language === 'fr' ? 'Deconnexion' : 'Logout'}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0 min-h-screen bg-[#0F1219]">
        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default OrbitLayout;
